"use client";

import { c as _c } from "react/compiler-runtime";
import { Maximize2, Minimize2 } from "lucide-react";
import type { ReactNode, RefObject } from "react";

/**
 * StageShell — the shared frame for an in-app media session (space meetings AND
 * direct calls): a FLOATING PANEL docked bottom-right by default, an EXPANDED
 * full-screen stage on demand, with the persistent hidden <audio> sink mounted
 * across the switch so audio never cuts. Purely presentational — each stage owns
 * its store, session engine, and slot content. Dark, single-look (a live session
 * wants focus), not theme-aware — extracted verbatim from the original
 * MeetingStage so the meetings surface is pixel-identical.
 */
export interface StageShellProps {
  /** Root testid — "meeting-stage" (frozen, e2e contract) or "call-stage". */
  testId: string;
  /** Stamped as data-phase for tests/debugging. */
  phase: string;
  ariaLabel: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** Testids for the toggle buttons — frozen for meetings, new for calls. */
  expandTestId: string;
  collapseTestId: string;
  /** The remote-audio sink (attach/detach handled by the stage). OPTIONAL: the
   *  call stage omits it — CallBridge owns an always-mounted sink so the element
   *  exists before any track arrives, even while no stage is rendered. */
  audioRef?: RefObject<HTMLAudioElement | null>;
  /** Header content per view (LiveDot + title/meta) — WITHOUT the toggle button,
   *  which the shell owns so its placement/testids stay consistent. */
  expandedHeader: ReactNode;
  panelHeader: ReactNode;
  /** Main content per view (roster / share video / status copy). */
  expandedBody: ReactNode;
  panelBody: ReactNode;
  /** The controls dock, rendered in the footer (expanded) / card tail (panel). */
  controls: ReactNode;
}
export function StageShell(t0) {
  const $ = _c(18);
  const {
    testId,
    phase,
    ariaLabel,
    expanded,
    onExpandedChange,
    expandTestId,
    collapseTestId,
    audioRef,
    expandedHeader,
    panelHeader,
    expandedBody,
    panelBody,
    controls
  } = t0;
  let t1;
  if ($[0] !== audioRef) {
    t1 = audioRef ? <audio ref={audioRef} autoPlay={true} hidden={true} /> : null;
    $[0] = audioRef;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== ariaLabel || $[3] !== collapseTestId || $[4] !== controls || $[5] !== expandTestId || $[6] !== expanded || $[7] !== expandedBody || $[8] !== expandedHeader || $[9] !== onExpandedChange || $[10] !== panelBody || $[11] !== panelHeader || $[12] !== phase || $[13] !== testId) {
    t2 = expanded ? <section data-testid={testId} data-phase={phase} data-view="expanded" aria-label={ariaLabel} className="fixed inset-0 z-120 flex flex-col bg-[#0b1020] text-white"><header className="flex items-center gap-3 px-5 py-4">{expandedHeader}<button type="button" onClick={() => onExpandedChange(false)} aria-label="Shrink to floating panel" title="Shrink" data-testid={collapseTestId} className="rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80"><Minimize2 className="h-5 w-5" /></button></header><div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-6 py-4">{expandedBody}</div><footer className="flex items-center justify-center pb-8 pt-2">{controls}</footer></section> : <section data-testid={testId} data-phase={phase} data-view="panel" aria-label={ariaLabel} className="fixed bottom-4 right-4 z-120 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-2xl bg-[#0b1020] p-4 text-white shadow-2xl ring-1 ring-white/10"><div className="flex items-center gap-2.5">{panelHeader}<button type="button" onClick={() => onExpandedChange(true)} aria-label="Expand to full screen" title="Expand" data-testid={expandTestId} className="rounded-full p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80"><Maximize2 className="h-4 w-4" /></button></div>{panelBody}{controls}</section>;
    $[2] = ariaLabel;
    $[3] = collapseTestId;
    $[4] = controls;
    $[5] = expandTestId;
    $[6] = expanded;
    $[7] = expandedBody;
    $[8] = expandedHeader;
    $[9] = onExpandedChange;
    $[10] = panelBody;
    $[11] = panelHeader;
    $[12] = phase;
    $[13] = testId;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== t1 || $[16] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}